/** @typedef {import('vue-router').RouteRecordRaw} RouteRecordRaw */
import { createRouter, createWebHistory } from 'vue-router';
import { findEnabledLocaleMatch } from '@/app/i18n';
import CustomerPhotoFromPhone from '@/components/customer/CustomerPhotoFromPhone.vue';
import CustomerPortraitsDownload from '@/components/customer/CustomerPortraitsDownload.vue';
import CustomerPortraitsDownloadCode from '@/components/customer/CustomerPortraitsDownloadCode.vue';
import StepArtStyle from '@/components/steps/ArtStyle.vue';
import StepFaces from '@/components/steps/Faces.vue';
import StepGDPRConsent from '@/components/steps/GDPRConsent.vue';
import StepGoPhotoScan from '@/components/steps/GoPhotoScan.vue';
import StepGoPhotoUpload from '@/components/steps/GoPhotoUpload.vue';
import StepHome from '@/components/steps/Home.vue';
import StepPaymentTerminal from '@/components/steps/PaymentTerminal.vue';
import StepPaymentType from '@/components/steps/PaymentType.vue';
import StepPaymentVoucher from '@/components/steps/PaymentVoucher.vue';
import StepPhotoFromPhone from '@/components/steps/PhotoFromPhone.vue';
import StepPhotoFromWebcam from '@/components/steps/PhotoFromWebcam.vue';
import StepPhotoPromptFromPhone from '@/components/steps/PhotoPromptFromPhone.vue';
import StepPortraits from '@/components/steps/Portraits.vue';
import StepPrintFailed from '@/components/steps/PrintFailed.vue';
import StepPrinting from '@/components/steps/Printing.vue';
import StepSuccess from '@/components/steps/Success.vue';
import { analytics } from '@/services/Analytics';
import { sentryService } from '@/services/telemetry/sentry';
import { PubSub } from '@/shared/lib/pub-sub';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import NotFoundView from '@/views/system/NotFoundView.vue';
import OutOfService from '@/views/system/OutOfServiceView.vue';

const toStepRoute = ([step, component]) => ({
  path: `/:slug/:localeCode/${step}`,
  component,
});

/** @type {RouteRecordRaw[]} */
const ROUTES = [
  /**
   * Customer-facing pages
   */
  {
    path: '/customer/portraits-download',
    component: CustomerPortraitsDownload,
  },
  {
    path: '/customer/portraits-download/:downloadCode',
    component: CustomerPortraitsDownloadCode,
  },
  {
    path: '/customer/photo-from-phone',
    component: CustomerPhotoFromPhone,
  },
  {
    path: '/customer/:pathMatch(.*)*',
    redirect: () => '/customer/portraits-download',
  },

  /**
   * Legacy download page redirects (from old kiosk version)
   * /portraits/ → /customer/portraits-download
   * /portraits/:code → /customer/portraits-download/:code
   *
   * Note: Sentry breadcrumbs are added in beforeEach guard to track legacy URL usage
   */
  {
    path: '/portraits',
    redirect: '/customer/portraits-download',
    meta: { isLegacyRedirect: true },
  },
  {
    path: '/portraits/',
    redirect: '/customer/portraits-download',
    meta: { isLegacyRedirect: true },
  },
  {
    path: '/portraits/:uuidPrefix',
    redirect: (to) => `/customer/portraits-download/${to.params.uuidPrefix}`,
    meta: { isLegacyRedirect: true },
  },

  /**
   * Kiosk steps
   */
  {
    path: '/:slug',
    component: StepHome,
  },
  {
    path: '/:slug/:localeCode',
    component: StepHome,
  },
  ...Object.entries({
    'gophoto-scan': StepGoPhotoScan,
    'gophoto-upload': StepGoPhotoUpload,
    'gdpr-consent': StepGDPRConsent,
    'art-style': StepArtStyle,
    'photo-from-webcam': StepPhotoFromWebcam,
    'photo-from-phone': StepPhotoFromPhone,
    'photo-prompt-from-phone': StepPhotoPromptFromPhone,
    faces: StepFaces,
  }).map(toStepRoute),
  {
    path: '/:slug/:localeCode/faces-individual',
    redirect: (to) => `/${to.params.slug}/${to.params.localeCode}/faces`,
  },
  {
    path: '/:slug/:localeCode/portraits',
    component: StepPortraits,
  },
  // The group step is gone and the individual step is now just
  // "portraits". A session sitting on either old URL when the kiosk
  // updated lands on the gallery instead of a dead route.
  ...Object.entries({
    'portraits-group': 'portraits',
    'portraits-individual': 'portraits',
  }).map(([from, to]) => ({
    path: `/:slug/:localeCode/${from}`,
    redirect: (r) => `/${r.params.slug}/${r.params.localeCode}/${to}`,
  })),
  ...Object.entries({
    printing: StepPrinting,
    'payment-type': StepPaymentType,
    'payment-terminal': StepPaymentTerminal,
    'payment-voucher': StepPaymentVoucher,
    success: StepSuccess,
    'print-failed': StepPrintFailed,
  }).map(toStepRoute),

  /**
   * Misc
   */
  {
    path: '/',
    redirect: () => '/404',
  },
  {
    path: '/404',
    component: NotFoundView,
  },
  {
    path: '/402',
    component: OutOfService,
  },
  {
    path: '/:catchAll(.*)',
    redirect: () => '/404',
  },
];

/**
 * Maximum consecutive locale-normalization redirects before the guard gives up.
 * Prevents infinite redirect loops on a 24/7 kiosk where a misconfigured
 * locale list could cause normalizeLocaleCode to be non-idempotent.
 */
const MAX_GUARD_REDIRECTS = 3;

/**
 * Keys that must never appear in parsed query params.
 * Prevents prototype pollution via URL query strings like `?__proto__=...`
 * or `?constructor=...` which can corrupt Object.prototype when assigned
 * to a plain object via bracket notation.
 * @type {Set<string>}
 */
const FORBIDDEN_QUERY_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

class Router extends PubSub {
  EVENTS = {
    STEP_CHANGED: 'STEP_CHANGED',
    LOCALE_CHANGED: 'LOCALE_CHANGED',
  };

  $router = null;

  /** @type {boolean} Guard against duplicate watchRouteChange() calls */
  _guardRegistered = false;

  /**
   * Consecutive locale-normalization redirects counter.
   * Reset to 0 on every navigation that does NOT redirect.
   * @type {number}
   */
  _guardRedirectCount = 0;

  get route() {
    return this.$router?.currentRoute?.value || {};
  }
  get path() {
    return this.route.path || '';
  }
  get slug() {
    return this.route.params?.slug;
  }
  get localeCode() {
    return this.route.params?.localeCode;
  }
  get step() {
    return this.getStepFromRoute(this.route);
  }
  get query() {
    return this.parseQueryParams(this.route.query);
  }
  get isStepPage() {
    return Boolean(this.slug);
  }
  get isCustomerFacingPage() {
    return /^\/customer($|\/)/.test(this.path);
  }

  constructor($router) {
    super();
    exposeToGlobalScope(this, 'Router');
    this.$router = $router;
    this.watchRouteChange();

    this.getLinkToStep = this.getLinkToStep.bind(this);
    this.getLinkToLocale = this.getLinkToLocale.bind(this);
    this.redirectToStep = this.redirectToStep.bind(this);
    this.redirectToLocale = this.redirectToLocale.bind(this);
  }

  watchRouteChange() {
    // Guard against duplicate registration.  On a 24/7 kiosk, a second
    // beforeEach guard would cause every navigation to be processed twice
    // (double STEP_CHANGED events, double analytics flush, etc.).
    if (this._guardRegistered) {
      console.warn(
        '[Router] watchRouteChange() called twice -- ignoring duplicate'
      );
      return;
    }
    this._guardRegistered = true;

    // Vue Router 4+ return-based guard (no `next` callback).
    // Returning undefined = proceed; returning a path = redirect.
    // This eliminates the race where an async guard calls next() on a
    // stale, already-cancelled navigation.
    this.$router.beforeEach((to, from) => {
      this._trackLegacyRedirects(to);
      this._flushAnalytics(from);

      // Skip locale processing for non-kiosk routes (/customer/*, /404, /402).
      if (!to.params.slug) {
        this._emitStepChange(to, from);
        this._guardRedirectCount = 0;
        return; // proceed
      }

      // Locale normalization: decide BEFORE allowing navigation.
      const redirect = this._resolveLocaleRedirect(to, from);
      if (redirect) {
        return redirect; // redirect to normalized URL
      }

      // Final navigation (no redirect needed) -- track step and locale changes.
      this._guardRedirectCount = 0;
      this._emitStepChange(to, from);
      this._emitLocaleChange(to, from);
      // Return undefined = proceed with navigation
    });
  }

  /**
   * Track legacy URL redirects (/portraits/* -> /customer/*) via Sentry breadcrumbs.
   * @param {import('vue-router').RouteLocationNormalized} to
   */
  _trackLegacyRedirects(to) {
    if (
      !to.redirectedFrom?.matched?.some(
        (record) => record.meta?.isLegacyRedirect
      )
    ) {
      return;
    }
    sentryService.addBreadcrumb(
      'navigation',
      'Legacy URL redirect',
      {
        legacy_path: to.redirectedFrom?.path,
        destination_path: to.path,
        has_download_code: Boolean(to.redirectedFrom?.params?.uuidPrefix),
      },
      'info'
    );
  }

  /**
   * Fire-and-forget analytics flush when leaving kiosk step pages.
   * @param {import('vue-router').RouteLocationNormalized} from
   */
  _flushAnalytics(from) {
    if (from.params?.slug) {
      analytics.flush().catch((error) => {
        console.error('[Router] Analytics flush failed:', error);
      });
    }
  }

  /**
   * Emit STEP_CHANGED event if the step changed between from and to routes.
   * @param {import('vue-router').RouteLocationNormalized} to
   * @param {import('vue-router').RouteLocationNormalized} from
   */
  _emitStepChange(to, from) {
    const stepTo = this.getStepFromRoute(to);
    const stepFrom = this.getStepFromRoute(from);
    if (stepFrom !== stepTo) {
      this.trigger(this.EVENTS.STEP_CHANGED, { from: stepFrom, to: stepTo });
      sentryService.setCurrentStep(stepTo || 'home');
    }
  }

  /**
   * Emit LOCALE_CHANGED event when the final (non-redirect) navigation changes locale.
   * Fires whenever the destination has a valid locale different from the source,
   * including transitions from slug-only kiosk routes.
   *
   * Must fire on direct navigations too, not just on the needsChangingLocale
   * redirect branch: a programmatic push from /slug/en-GB/faces to
   * /slug/fr-FR/faces doesn't need normalization but still changes locale,
   * and listeners that depend on LOCALE_CHANGED would otherwise see stale state.
   *
   * @param {import('vue-router').RouteLocationNormalized} to
   * @param {import('vue-router').RouteLocationNormalized} from
   */
  _emitLocaleChange(to, from) {
    const localeCodeTo = this.normalizeLocaleCode(to.params.localeCode);
    const localeCodeFrom = this.normalizeLocaleCode(from.params.localeCode);

    if (!localeCodeTo || localeCodeTo === localeCodeFrom) {
      sentryService.addBreadcrumb(
        'router',
        'Locale change skipped',
        {
          raw_locale_to: to.params.localeCode,
          raw_locale_from: from.params.localeCode,
          normalized_to: localeCodeTo || 'undefined',
          normalized_from: localeCodeFrom || 'undefined',
          reason: !localeCodeTo ? 'to_undefined' : 'same_locale',
          to_path: to.path,
          from_path: from.path,
        },
        'warning'
      );
      return;
    }

    this.trigger(this.EVENTS.LOCALE_CHANGED, {
      from: localeCodeFrom,
      to: localeCodeTo,
    });
    sentryService.setLocale(localeCodeTo);
  }

  /**
   * Determine whether the guard needs to redirect for locale normalization.
   * Returns a redirect path string, or null if no redirect is needed.
   *
   * Handles two redirect scenarios:
   *  1. Locale code needs normalization (e.g., 'en' -> 'en-GB')
   *  2. Target locale doesn't exist (fall back to source locale)
   *
   * Includes infinite-redirect safety valve: after MAX_GUARD_REDIRECTS
   * consecutive redirects, stops redirecting and returns null to prevent
   * the browser from hanging on a 24/7 kiosk.
   *
   * @param {import('vue-router').RouteLocationNormalized} to
   * @param {import('vue-router').RouteLocationNormalized} from
   * @returns {string|null} Redirect path or null to proceed
   */
  _resolveLocaleRedirect(to, from) {
    const localeCodeFrom = this.normalizeLocaleCode(from.params.localeCode);
    const localeCodeTo = this.normalizeLocaleCode(to.params.localeCode);

    const redirectedToNonexistentLocale = Boolean(
      to.params.localeCode && localeCodeFrom && !localeCodeTo
    );
    const needsNormalizingLocaleCode = Boolean(
      localeCodeTo && to.params.localeCode !== localeCodeTo
    );

    // NOTE: "needsChangingLocale" (languageFrom !== languageTo) was intentionally
    // removed from needsRedirect. It triggered a redirect to the exact same path
    // as `to` (since redirectLocale = localeCodeTo), creating an infinite loop
    // until MAX_GUARD_REDIRECTS kicked in (Sentry: KIOSK-FRONTEND-9J, 51 users).
    // LOCALE_CHANGED is already emitted by _emitLocaleChange() on the non-redirect
    // path, so removing this branch has no effect on locale change notifications.
    const needsRedirect =
      needsNormalizingLocaleCode || redirectedToNonexistentLocale;

    if (!needsRedirect) {
      return null;
    }

    // Infinite-redirect safety valve.
    this._guardRedirectCount++;
    if (this._guardRedirectCount > MAX_GUARD_REDIRECTS) {
      console.error(
        `[Router] Locale redirect loop detected (${this._guardRedirectCount} consecutive redirects). ` +
          `Proceeding without redirect. to=${to.path}, from=${from.path}`
      );
      sentryService.captureMessage('Router locale redirect loop detected', {
        tags: { router_bug: 'redirect_loop' },
        extra: {
          redirect_count: this._guardRedirectCount,
          to_path: to.path,
          from_path: from.path,
          locale_code_to: localeCodeTo,
          locale_code_from: localeCodeFrom,
          raw_locale_to: to.params.localeCode,
        },
        level: 'error',
      });
      this._guardRedirectCount = 0;
      return null; // suppress redirect, proceed with navigation
    }

    const stepTo = this.getStepFromRoute(to);
    const redirectLocale = redirectedToNonexistentLocale
      ? localeCodeFrom
      : localeCodeTo;
    return this.getLinkToStep(stepTo, {
      slug: to.params.slug,
      localeCode: redirectLocale,
      query: this.parseQueryParams(to.query),
    });
  }

  normalizeLocaleCode(rawLocaleCode) {
    if (!rawLocaleCode) {
      return undefined;
    }
    const localeMatch = findEnabledLocaleMatch(rawLocaleCode);
    const locale = localeMatch.precise || localeMatch.approximate;
    return locale?.code;
  }

  /**
   * Parse Vue Router query object values.  Values arrive as strings;
   * this attempts JSON.parse on each to recover booleans and numbers.
   *
   * Security: Only primitive types (boolean, number, null) are recovered.
   * Objects and arrays from JSON.parse are rejected and kept as raw strings
   * to prevent type-confusion bugs in components that expect string query
   * values, and to block injection of structured data via URL params.
   *
   * @param {Record<string, string>|null|undefined} rawQuery
   * @returns {Record<string, unknown>}
   */
  parseQueryParams(rawQuery) {
    if (!rawQuery || typeof rawQuery !== 'object') {
      return {};
    }
    const parsedQuery = {};
    for (const key in rawQuery) {
      if (!Object.hasOwn(rawQuery, key)) continue;
      // Reject prototype-polluting keys (e.g., ?__proto__=... in URL)
      if (FORBIDDEN_QUERY_KEYS.has(key)) continue;
      try {
        const parsed = JSON.parse(rawQuery[key]);
        // Only accept primitives (boolean, number, null).
        // Reject objects/arrays to prevent type-confusion and injection
        // of structured data through URL query parameters.
        if (parsed !== null && typeof parsed === 'object') {
          parsedQuery[key] = rawQuery[key];
        } else {
          parsedQuery[key] = parsed;
        }
      } catch (_e) {
        parsedQuery[key] = rawQuery[key];
      }
    }
    return parsedQuery;
  }

  /**
   * @param {Record<string, unknown>} rawQuery
   * @returns {Record<string, string>}
   */
  jsonifyQueryParams(rawQuery) {
    if (!rawQuery || typeof rawQuery !== 'object') {
      return {};
    }
    /** @type {Record<string, string>} */
    const jsonifiedQuery = {};
    for (const key in rawQuery) {
      if (!Object.hasOwn(rawQuery, key)) continue;
      if (FORBIDDEN_QUERY_KEYS.has(key)) continue;
      if (typeof rawQuery[key] === 'string') {
        jsonifiedQuery[key] = rawQuery[key];
      } else {
        jsonifiedQuery[key] = JSON.stringify(rawQuery[key]);
      }
    }
    return jsonifiedQuery;
  }

  /**
   * Extract the step name from a route by stripping the `/:slug/:localeCode/`
   * prefix from the path.
   *
   * Uses string-based prefix removal instead of RegExp to avoid regex injection
   * from user-controlled slug/localeCode params (slugs like `my.kiosk+test`
   * contain regex-special characters that corrupt the match).
   *
   * @param {import('vue-router').RouteLocationNormalized|Object} route
   * @returns {string|null} Step name (e.g. 'faces', 'printing') or null for non-kiosk routes
   */
  getStepFromRoute(route) {
    const { path = '', params = {} } = route || {};
    const { slug, localeCode } = params;
    if (!slug) {
      return null;
    }

    // Build the expected prefix literally (no regex) to avoid injection.
    // Handles both `/:slug/:localeCode/step` and `/:slug` (home without locale).
    const prefixWithLocale = localeCode ? `/${slug}/${localeCode}/` : null;
    const prefixWithLocaleNoTrail = localeCode
      ? `/${slug}/${localeCode}`
      : null;
    const prefixSlugOnly = `/${slug}/`;
    const prefixSlugExact = `/${slug}`;

    if (prefixWithLocale && path.startsWith(prefixWithLocale)) {
      return path.slice(prefixWithLocale.length);
    }
    if (prefixWithLocaleNoTrail && path === prefixWithLocaleNoTrail) {
      return '';
    }
    if (path.startsWith(prefixSlugOnly)) {
      return path.slice(prefixSlugOnly.length);
    }
    if (path === prefixSlugExact) {
      return '';
    }
    // All Vue Router matched routes produce paths in one of the four formats
    // above. If none matched, the path is unexpected -- return '' (home)
    // rather than attempting regex extraction with user-controlled params
    // (slug values could otherwise leak into the regex on edge cases).
    return '';
  }

  /**
   * Build an absolute path for a kiosk step.
   *
   * @param {string} stepId - Step name (e.g. 'faces') or '' for home
   * @param {Object} options
   * @param {string} [options.localeCode] - Override locale code
   * @param {string} [options.slug] - Override kiosk slug
   * @param {Record<string, unknown>} [options.query] - Query params
   * @param {boolean} [options.omitQuery] - Strip query string
   * @returns {string} Absolute path like `/my-kiosk/en-GB/faces`
   */
  getLinkToStep(stepId, options = {}) {
    const { localeCode, slug, query, omitQuery } = options;

    const effectiveSlug = slug || this.slug;
    if (!effectiveSlug) {
      // No slug available (e.g., on /404 or /customer/* page).
      // Return a safe fallback instead of producing `/undefined/...` which
      // would match the /:slug catch-all and cause a confusing navigation.
      console.warn(
        '[Router] getLinkToStep called without slug, returning /404'
      );
      return '/404';
    }

    // Validate slug does not contain path separators or control characters.
    // Vue Router URL-decodes params, so a crafted URL like /%2F%2F/en-GB/faces
    // would produce slug='//'. Without validation this breaks the path structure
    // and could redirect to unexpected origins or paths.
    if (/[/\\\x00-\x1f]/.test(effectiveSlug)) {
      console.error(
        '[Router] getLinkToStep: invalid slug containing path separators, returning /404'
      );
      return '/404';
    }

    const localeCodeNormalized = this.normalizeLocaleCode(
      localeCode || this.localeCode
    );

    // Build path.  Avoid trailing slash for home (stepId is '' or falsy)
    // to produce `/slug/locale` instead of `/slug/locale/`.
    // When localeCodeNormalized is undefined (locale list not initialized yet,
    // or raw code has no match), omit the locale segment entirely to produce
    // `/:slug` (home route) rather than `/slug/undefined/step`.
    const step = stepId || '';
    let basePath;
    if (!localeCodeNormalized) {
      // No resolved locale -- navigate to slug-only home route
      basePath = step ? `/${effectiveSlug}/${step}` : `/${effectiveSlug}`;
    } else {
      basePath = step
        ? `/${effectiveSlug}/${localeCodeNormalized}/${step}`
        : `/${effectiveSlug}/${localeCodeNormalized}`;
    }

    let queryString;
    if (!omitQuery) {
      const queryParams = query || this.query;
      queryString = new URLSearchParams(
        this.jsonifyQueryParams(queryParams)
      ).toString();
    }
    return queryString ? `${basePath}?${queryString}` : basePath;
  }

  getLinkToLocale(localeCode, options = {}) {
    return this.getLinkToStep(this.step, { ...options, localeCode });
  }

  /**
   * Navigate to an arbitrary path.
   *
   * Returns the router.push promise and catches NavigationDuplicated /
   * NavigationCancelled errors that are expected on a kiosk with rapid
   * tapping. Without this catch, every double-tap produces an unhandled
   * promise rejection that hits Sentry.
   *
   * @param {string} path
   * @returns {Promise<import('vue-router').NavigationFailure|void|undefined>}
   */
  redirectToPath(path) {
    return this.$router.push(path).catch(this._handleNavigationError);
  }

  /**
   * Navigate to a kiosk step by name.
   * @param {string} stepId
   * @param {Object} [options]
   * @returns {Promise<import('vue-router').NavigationFailure|void|undefined>}
   */
  redirectToStep(stepId, options) {
    return this.$router
      .push(this.getLinkToStep(stepId, options))
      .catch(this._handleNavigationError);
  }

  /**
   * Navigate to the current step with a different locale.
   * @param {string} localeCode
   * @param {Object} [options]
   * @param {boolean} [options.silent] - Use replace instead of push
   * @returns {Promise<import('vue-router').NavigationFailure|void|undefined>}
   */
  redirectToLocale(localeCode, options) {
    const link = this.getLinkToLocale(localeCode);
    const nav = options?.silent
      ? this.$router.replace(link)
      : this.$router.push(link);
    return nav.catch(this._handleNavigationError);
  }

  /**
   * Swallow expected navigation errors (duplicate, cancelled, aborted).
   * On a 24/7 kiosk, rapid tapping routinely produces these; they are not
   * bugs and must not reach the global unhandledrejection handler / Sentry.
   * Re-throws genuinely unexpected errors.
   *
   * @param {Error} error
   */
  _handleNavigationError(error) {
    // Vue Router 4 NavigationFailureType enum values (from source):
    //   aborted    = 4   (guard returned false)
    //   cancelled  = 8   (new navigation before previous completed)
    //   duplicated = 16  (already at the target location)
    // All navigation failures have a numeric `.type` property.
    // On a kiosk with rapid tapping, duplicated and cancelled are routine.
    const { type } = error || {};
    if (type === 4 || type === 8 || type === 16) {
      return; // expected on a kiosk -- not a bug
    }
    // Genuinely unexpected error -- re-throw so it hits Sentry.
    throw error;
  }
}

const vueRouter = createRouter({
  history: createWebHistory(),
  routes: ROUTES,
});

const router = new Router(vueRouter);

export { router, vueRouter };
